import React, { useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useCommunityInitialization } from 'hooks/useCommunityInitialization';
import {
  getAllowedExternalRedirect,
  getAuthUrl,
  getForumsSignInUrl,
  getForumsSignOutUrl,
} from '../../utils/url-generator';
import { UserContext } from '../../context/UserContext';
import { ConsentValidator, initializeCommunity } from '@atlassian/community-library';
import WelcomeIllustration from '../../assets/images/welcome-image.png';
import ExternalRedirect from '../common/ExternalRedirect';
import { sendNewAccountEvent } from '../../utils/send-newaccount-event';
import ErrorPage from '../error/Error';
import { AppLoadingContext } from '../../context/AppLoadingContext';

enum PageInitializationState {
  NOT_INITIALIZED = 'NOT_INITIALIZED',
  INIT_LOADING = 'INIT_LOADING',
  INIT_COMPLETED = 'INIT_COMPLETED',
  NOT_CONSENTED = 'NOT_CONSENTED',
  REDIRECT_URL = 'REDIRECT_URL',
}

type InitValidatorProps = {
  children: React.ReactNode;
};

const typedGlobalThis = globalThis as typeof globalThis & {
  __IS_CONSENTED__: boolean;
  __ACCOUNTS_INITIALIZED__: boolean;
  isSSR: boolean;
  __IS_NEW_FORUMS_PROFILE__: boolean;
};

export const InitValidator = ({ children }: InitValidatorProps) => {
  const { isChecking, userHasConsented, userIsInitialized, initializationError } = useCommunityInitialization();
  const userData = useContext(UserContext);
  const {loading, setLoading} = useContext(AppLoadingContext);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSignupAnalyticsSent, setIsSignupAnalyticsSent] = useState<boolean>(userIsInitialized);
  const [pageInitState, setPageInitState] = useState<PageInitializationState>(PageInitializationState.NOT_INITIALIZED);
  const isLocal = process.env['REACT_APP_ENVI'] === 'local';
  const homepage = isLocal ? 'http://localhost:9000' : process.env['HOME_BASE_URL'];

  // Check for SSR state to avoid unnecessary consent checks
  const isSSR = typedGlobalThis.isSSR;
  const ssrUserConsented = typedGlobalThis.__IS_CONSENTED__;
  const ssrUserInitialized = typedGlobalThis.__ACCOUNTS_INITIALIZED__;

  // Persist redirectParam across renders
  const searchParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const initialRedirectParam = searchParams.get('redirect');
  const redirectParamRef = useRef(initialRedirectParam);

  const initializeAndRedirect = async () => {
    try {
      if (!userData?.user?.account_id && !userData?.user?.email) {
        throw new Error('User account ID is required');
      }
      const response = await initializeCommunity(userData?.user?.email, homepage, homepage);

      if (!response) {
        console.error('[useCommunityInitialization] Community initialization failed - no response');
        throw new Error('could not initialize community');
      } else {
        await sendNewAccountEvent(response?.isNewForumsProfile, userData?.user?.account_id);
        setIsSignupAnalyticsSent(true);
      }

      handleRedirect();
    } catch (error) {
      console.error('[useCommunityInitialization] Error initializing community:', error);
      throw new Error('could not initialize community');
      // redirect to error page.
    }
  };

  // Handle redirect to forums or homepage
  const handleRedirect = useCallback((shouldLogout = false) => {
    if (typeof window === 'undefined') return;

    const redirectParam = redirectParamRef.current;
    const currentPath = window.location.pathname || '/';
    const fullRedirectUrl = `${process.env['HOME_BASE_URL']}${redirectParam ? redirectParam : '/'}`;

    // TEMP (pre-CloudFront /forums migration): if the redirect is an allowlisted
    // external HLV URL, honour it verbatim instead of treating it as a same-origin
    // path. Skipped for logout, which has its own identity-logout handling below.
    if (!shouldLogout) {
      const externalRedirect = getAllowedExternalRedirect(redirectParam);
      if (externalRedirect) {
        setRedirectUrl(externalRedirect);
        return;
      }
    }

    const handleForumsLogout = () => {
      const forumsLogoutUrl = getForumsSignOutUrl(`${process.env['KHOROS_ENDPOINT_PATH']}`);
      const forumsLogoutAbsoluteUrl = `${process.env['HOME_BASE_URL']}/${forumsLogoutUrl}`;
      const identityLogoutUrl = getAuthUrl('logout', {
        redirectUrl: forumsLogoutAbsoluteUrl,
        experience: 'entry-auth',
        email: userData?.user?.email,
      });
      setRedirectUrl(identityLogoutUrl);
    };

    const handleNonForumsLogout = () => {
      const logoutPath = getAuthUrl('logout', {
        redirectUrl: `${process.env['HOME_BASE_URL']}/`,
        experience: 'entry-auth',
        email: userData?.user?.email,
      });
      setRedirectUrl(logoutPath);
    };

    const handleForumsConsent = () => {
      const forumsSignInUrl = getForumsSignInUrl({
        redirectUrl: fullRedirectUrl,
        email: userData?.user?.email,
      });
      setRedirectUrl(`${process.env['HOME_BASE_URL']}${forumsSignInUrl}`);
    };

    const handleNonForumsConsent = () => {
      setRedirectUrl(`${process.env['HOME_BASE_URL']}/`);
    };

    const handleDefaultLogout = () => {
      const logoutPath = getAuthUrl('logout', {
        redirectUrl: `${process.env['HOME_BASE_URL']}${currentPath}`,
        experience: 'entry-auth',
        email: userData?.user?.email,
      });
      setRedirectUrl(logoutPath);
    };

    const handleDefaultConsent = () => {
      setRedirectUrl(`${process.env['HOME_BASE_URL']}${currentPath}`);
    };

    if (redirectParam) {
      if (shouldLogout) {
        if (redirectParam.includes('/forums')) {
          handleForumsLogout();
        } else {
          handleNonForumsLogout();
        }
      } else {
        if (redirectParam.includes('/forums')) {
          handleForumsConsent();
        } else {
          handleNonForumsConsent();
        }
      }
    } else {
      if (shouldLogout) {
        handleDefaultLogout();
      } else {
        handleDefaultConsent();
      }
    }
  }, [userData?.user?.email, redirectParamRef]);

  useEffect(() => {
    if (userData !== undefined) {
      (async () => {
        if (ssrUserInitialized) {
          try {
            await sendNewAccountEvent(typedGlobalThis.__IS_NEW_FORUMS_PROFILE__ ?? false, userData?.user?.account_id);
          } catch (error) {
            console.error('[InitValidator] Error sending new account event:', error);
          }
        }
        setIsSignupAnalyticsSent(true);
        setIsLoading(false);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // Memoize the page state determination logic to prevent unnecessary recalculations
  const getPageState = useCallback(() => {
    // Priority 1: Check for initialization error first
    if (initializationError) {
      return; // Exit early if there's an error
    }
    if (redirectUrl) {
      return;
    }
    // Priority 2: SSR users who are already consented and initialized
    if (isSSR && ssrUserConsented && ssrUserInitialized) {
      if (redirectParamRef.current) {
        return PageInitializationState.REDIRECT_URL;
      } else {
        return PageInitializationState.INIT_COMPLETED;
      }
    }
    // Priority 3: While checking consent status or analytics not sent show loader
    if (isChecking || !isSignupAnalyticsSent) {
      return PageInitializationState.INIT_LOADING;
    }
    // Priority 4: Still loading user data
    if (isLoading) {
      return PageInitializationState.INIT_LOADING;
    }
    // Priority 5: No user data available
    if (!userData?.user) {
      return PageInitializationState.INIT_COMPLETED;
    }
    // Priority 6: User consented and initialized with redirect
    if (userHasConsented && userIsInitialized && redirectParamRef.current) {
      return PageInitializationState.REDIRECT_URL;
    }
    // Priority 7: User has not consented
    if (!userHasConsented) {
      return PageInitializationState.NOT_CONSENTED;
    }
    // Priority 8: User consented and initialized, ready to show content
    if (userIsInitialized) {
      if (redirectParamRef.current) {
        return PageInitializationState.REDIRECT_URL;
      } else {
        return PageInitializationState.INIT_COMPLETED;
      }
    }
    // Default: initialization in progress
    return PageInitializationState.INIT_LOADING;
  }, [
    initializationError,
    isSSR,
    ssrUserConsented,
    ssrUserInitialized,
    isChecking,
    isSignupAnalyticsSent,
    isLoading,
    userData?.user,
    userHasConsented,
    userIsInitialized,
    redirectUrl
  ]);

  useEffect(() => {
    const currentPageState = getPageState();
    if(currentPageState === PageInitializationState.REDIRECT_URL) {
      handleRedirect();
      setPageInitState(currentPageState);
    } else if (currentPageState !== undefined) {
      setPageInitState(currentPageState);
    }
  }, [getPageState, handleRedirect]);

  // Update loading state in useEffect to avoid setState during render
  useEffect(() => {
    if(pageInitState === PageInitializationState.INIT_LOADING && loading.initValidator === false){
      setLoading((prev) => ({
        initValidator: true,
        servicesInitializer: prev.servicesInitializer
      }))
    } else if (pageInitState === PageInitializationState.INIT_COMPLETED && loading.initValidator === true) {
      setLoading((prev) => ({
        initValidator: false, 
        servicesInitializer: prev.servicesInitializer
      }))
    } else if (pageInitState === PageInitializationState.NOT_CONSENTED && (loading.initValidator === true || loading.servicesInitializer === true)) {
      setLoading(() => ({
        initValidator: false, 
        servicesInitializer: false
      }))
    }
  }, [pageInitState, loading, setLoading]);

  // If we have a redirect URL, use ExternalRedirect
  if (redirectUrl) {
    return <ExternalRedirect to={redirectUrl} />;
  }

  // If there was an initialization error, show error page
  if (initializationError) {
    return <ErrorPage />;
  }

  // Show consent validator if user has not consented
  if (pageInitState === PageInitializationState.NOT_CONSENTED) {
    return (
      <ConsentValidator
        onLogout={() => {
          handleRedirect(true);
        }}
        onConsentError={() => {
          handleRedirect();
        }}
        onConsentSuccess={() => {
          initializeAndRedirect();
        }}
        aaid={userData?.user?.account_id || 'test-user-id'}
        isUserConsented={false}
        coverImageSrc={WelcomeIllustration}
      >
        <AfterConsentRedirect onConsentGiven={() => {}} />
      </ConsentValidator>
    );
  }

  return <>{children}</>;
};

// Add AfterConsentRedirect component
const AfterConsentRedirect = ({ onConsentGiven }: { onConsentGiven: () => void }) => {
  // This effect will run as soon as the modal closes (i.e., after consent is accepted)
  useEffect(() => {
    onConsentGiven();
  }, [onConsentGiven]);
  return null;
};
